import type { ServiceStatus, Status, StatusPayload } from '@artifax/shared';
import { REGION_KEYS } from '@artifax/shared';

/** Fetch the current status snapshot from the backend. */
export async function fetchStatus(signal?: AbortSignal): Promise<StatusPayload> {
  const res = await fetch('/api/status', { signal, headers: { accept: 'application/json' } });
  if (!res.ok) {
    throw new Error(`status request failed: ${res.status}`);
  }
  return suppressMaintenance((await res.json()) as StatusPayload);
}

/**
 * Coalesce `maintenance` → `operational` for display.
 *
 * We pause StatusCake during maintenance rather than treating it as a service
 * state, so maintenance is not surfaced in the UI. The backend still models and
 * can emit `maintenance` (see apps/server .../mapping.ts `applyMaintenance`);
 * this suppression lives only at the UI boundary so nothing downstream ever
 * renders it. To bring planned-maintenance display back, remove this transform
 * and restore the Maintenance row in constants.ts `TIER_KEY`.
 */
const displayStatus = (s: Status): Status => (s === 'maintenance' ? 'operational' : s);

const stripService = (svc: ServiceStatus): ServiceStatus => ({
  ...svc,
  status: displayStatus(svc.status),
  history: svc.history.map((d) => ({ ...d, status: displayStatus(d.status) })),
});

function suppressMaintenance(payload: StatusPayload): StatusPayload {
  const regions = {} as StatusPayload['regions'];
  for (const key of REGION_KEYS) {
    regions[key] = { ...payload.regions[key], event: stripService(payload.regions[key].event) };
  }
  return {
    ...payload,
    overall: displayStatus(payload.overall),
    regions,
    global: { agora: stripService(payload.global.agora) },
  };
}
